/**
 * Sound cues (MM #114).
 *
 * A show's sound effects come from `cueCode`: a JS snippet the engine runs ONCE
 * to produce `[{time, sound, volume}]`. It is not a list — the two shipped shows
 * that use it generate their cues procedurally, with accelerating intervals and
 * section lengths computed from nine parameters. Section_Battle's booms speed up
 * through a `while` loop; Color_Breathe pre-computes where each section lands.
 *
 * So the studio EXECUTES cue code rather than parsing it, the same way the wall
 * track is sampled rather than assumed. You see exactly where every hit falls
 * against the blocks — and because the cues depend on params, lowering an
 * interval visibly compresses them.
 *
 * Editing is offered only when the cues ARE a literal list, which is what a show
 * authored in the studio produces. Rewriting a procedural cueCode as a flat array
 * would silently sever every parameter it reads — the same trap as flattening a
 * palette identifier to a hex.
 */

/** Matches a cue list written literally, i.e. `return [ {...}, {...} ];` */
const LITERAL = /^\s*(?:\/\/[^\n]*\n|\s)*return\s*(\[[\s\S]*\])\s*;?\s*$/;

/**
 * Run cue code and return its cues.
 *
 * Mirrors AudioCueEngine.buildCues so the studio and the wall agree. Returns []
 * on any failure rather than throwing: bad cue code should cost the sound
 * preview, not the whole editor.
 *
 * @returns {{cues: Array, error: string|null}}
 */
export function buildCues(cueCode, { params = {}, zoneIndex = 0, zoneCount = 0, duration = 0 } = {}) {
  if (!cueCode || !String(cueCode).trim()) return { cues: [], error: null };
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('params', 'zoneIndex', 'zoneCount', 'duration', `"use strict";\n${cueCode}`);
    const out = fn(params, zoneIndex, zoneCount, duration);
    if (!Array.isArray(out)) return { cues: [], error: 'cue code did not return an array' };
    const cues = out
      .filter((c) => c && Number.isFinite(Number(c.time)) && c.sound)
      .map((c) => ({
        time: Number(Number(c.time).toFixed(3)),
        sound: String(c.sound),
        volume: Number.isFinite(Number(c.volume)) ? Number(c.volume) : 1,
      }))
      .sort((a, b) => a.time - b.time);
    return { cues, error: null };
  } catch (err) {
    return { cues: [], error: err.message };
  }
}

/**
 * Can the studio edit these cues as a list?
 *
 * True only for a bare `return [ ... ];`. Anything that computes is left alone
 * and shown read-only — see the note at the top.
 */
export function isEditableCueCode(cueCode) {
  if (!cueCode || !String(cueCode).trim()) return true;   // empty: a new list can be written
  return LITERAL.test(String(cueCode));
}

/** Write a cue list back as code the engine runs unchanged. */
export function serialiseCueList(cues) {
  if (!cues?.length) return 'return [];\n';
  const rows = cues
    .slice()
    .sort((a, b) => a.time - b.time)
    .map((c) => {
      const vol = c.volume === 1 || c.volume == null ? '' : `, volume: ${Number(c.volume)}`;
      return `  { time: ${Number(c.time)}, sound: '${c.sound}'${vol} },`;
    });
  return `return [\n${rows.join('\n')}\n];\n`;
}

/**
 * Add a cue, keeping the list in time order.
 *
 * Two cues of the same clip within MERGE seconds are treated as one: at that
 * spacing the second is inaudible as a separate hit, and it is almost always a
 * double-click rather than an intent to fire twice.
 */
export const CUE_MERGE = 0.05;

export function addCue(cues, { time, sound, volume = 1 }, merge = CUE_MERGE) {
  if (!Number.isFinite(Number(time)) || Number(time) < 0 || !sound) return cues || [];
  const t = Number(Number(time).toFixed(3));
  const kept = (cues || []).filter((c) => !(c.sound === sound && Math.abs(c.time - t) <= merge));
  return [...kept, { time: t, sound, volume: Number(volume) }].sort((a, b) => a.time - b.time);
}

/** Remove the cue nearest a time, within a radius. Returns the list unchanged if none is close. */
export function removeCueNear(cues, time, radius = 0.25) {
  const list = cues || [];
  if (!list.length) return list;
  const nearest = list.reduce((best, c) => (
    Math.abs(c.time - time) < Math.abs(best.time - time) ? c : best
  ), list[0]);
  if (Math.abs(nearest.time - time) > radius) return list;
  return list.filter((c) => c !== nearest);
}

/** Which clips a cue list actually uses, with hit counts, most-used first. */
export function clipUsage(cues) {
  const counts = new Map();
  (cues || []).forEach((c) => counts.set(c.sound, (counts.get(c.sound) || 0) + 1));
  return [...counts.entries()]
    .map(([sound, count]) => ({ sound, count }))
    .sort((a, b) => b.count - a.count || a.sound.localeCompare(b.sound));
}

/**
 * Cues naming a clip the show does not include.
 *
 * The endpoint resolves audioClipKeys to URLs, so a cue pointing at anything
 * else is silently dropped at the event — no error, just a sound that never
 * plays. Worth surfacing while there is still time to fix it.
 */
export function missingClips(cues, audioClipKeys = []) {
  const have = new Set(audioClipKeys || []);
  return clipUsage(cues).filter((u) => !have.has(u.sound)).map((u) => u.sound);
}

/** Clips the show carries but no cue ever plays. */
export function unusedClips(cues, audioClipKeys = []) {
  const used = new Set((cues || []).map((c) => c.sound));
  return (audioClipKeys || []).filter((k) => !used.has(k));
}

/**
 * Which cues fall in the window the playhead just crossed.
 *
 * Half-open (prev, next] so a cue fires exactly once as the playhead passes it,
 * and never twice at a frame boundary. Returns nothing when time moved
 * backwards or jumped — a scrub is not playback, and firing every cue between
 * here and there would be a burst of noise.
 */
export function cuesInWindow(cues, prev, next, maxJump = 1) {
  if (!(next > prev) || next - prev > maxJump) return [];
  return (cues || []).filter((c) => c.time > prev && c.time <= next);
}

